/*
 * @Author: Ryan
 * @Date: 2025-12-25
 * @Description: Real-world use cases section with accordion-style tabs and video player
 */
import React, { useRef, useState } from 'react';

interface UseCase {
  id: string;
  title: string;
  description: string;
  videoUrl: string;
  posterUrl: string;
}

const useCases: UseCase[] = [
  {
    id: 'marketing',
    title: 'Marketing & Advertising',
    description:
      'Brands can quickly create campaign videos, product explainers, or promotional offers without expensive shoots. With AI avatars speaking in multiple languages, global campaigns can launch in days instead of weeks. Perfect for social ads, landing pages, and brand storytelling.',
    videoUrl:
      'https://assets.falcocut.ai/landing-page/ai-avatar-generator/marketingandadvertisingavatarvideo_1787467983032717312.mp4',
    posterUrl:
      'https://assets.falcocut.ai/landing-page/ai-avatar-generator/ai-avatar-use-case-marketing.png',
  },
  {
    id: 'training',
    title: 'Corporate Training',
    description:
      'HR and L&D teams turn onboarding, compliance, and internal updates into engaging training videos. Digital avatars keep messaging consistent across regions, while multilingual dubbing ensures every employee gets the same clear instructions.',
    videoUrl:
      'https://assets.falcocut.ai/landing-page/ai-avatar-generator/corporatetrainingavatarvideo_1787468087617691648.mp4',
    posterUrl:
      'https://assets.falcocut.ai/landing-page/ai-avatar-generator/ai-avatar-use-case-ecommerce.png',
  },
  {
    id: 'ecommerce',
    title: 'E-commerce',
    description:
      'Online stores use avatars as virtual spokespeople to present products, highlight features, and build buyer trust. Instead of static product pages, talking avatars make demos interactive, persuasive, and accessible to customers worldwide.',
    videoUrl:
      'https://assets.falcocut.ai/landing-page/ai-avatar-generator/ecommerceavatarvideo_1787468078688014336.mp4',
    posterUrl:
      'https://assets.falcocut.ai/landing-page/ai-avatar-generator/ai-avatar-use-case-corporate-training.png',
  },
  {
    id: 'education',
    title: 'Education & Online Courses',
    description:
      'Teachers and course creators use avatars to turn lessons, tutorials, and e-learning modules into dynamic videos. Avatars can explain complex topics in simple language, making knowledge more engaging and easier to remember for global students.',
    videoUrl:
      'https://assets.falcocut.ai/landing-page/ai-avatar-generator/educationandonlinecoursesavatarvideo_1787467983024332800.mp4',
    posterUrl:
      'https://assets.falcocut.ai/landing-page/ai-avatar-generator/ai-avatar-use-case-content-creators.png',
  },
  {
    id: 'creators',
    title: 'Content Creators & Influencers',
    description:
      'YouTubers, TikTokers, and Instagram creators scale content without always appearing on camera. Avatars can read scripts, deliver commentary, or act as digital co-hosts—helping creators produce more videos, reach global audiences, and stay consistent across platforms.',
    videoUrl:
      'https://assets.falcocut.ai/landing-page/ai-avatar-generator/contentcreatorsandinfluencersvideo_1787468115467870208.mp4',
    posterUrl:
      'https://assets.falcocut.ai/landing-page/ai-avatar-generator/ai-avatar-use-case-education.png',
  },
];

const PlayButton: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
  >
    <rect x="1" y="1" width="62" height="62" rx="31" fill="white" fillOpacity="0.1" />
    <rect x="1" y="1" width="62" height="62" rx="31" stroke="white" strokeWidth="2" />
    <path
      d="M41.5038 30.3359C42.6913 31.1275 42.6913 32.8725 41.5038 33.6641L29.1094 41.9271C27.7803 42.8131 26 41.8603 26 40.2629L26 23.7371C26 22.1397 27.7803 21.1869 29.1094 22.0729L41.5038 30.3359Z"
      fill="white"
    />
  </svg>
);

interface UseCaseTabProps {
  useCase: UseCase;
  isActive: boolean;
  onClick: () => void;
}

const UseCaseTab: React.FC<UseCaseTabProps> = ({ useCase, isActive, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-2xl transition-colors duration-300 p-4 pad:p-5 ${
        isActive
          ? 'bg-[#7C3AED] text-white'
          : 'bg-[#F5F5F7] text-gray-700 hover:bg-[#EBEBED]'
      }`}
    >
      <h3
        className={`font-semibold text-base pad:text-base ${isActive ? 'text-white' : 'text-gray-800'}`}
      >
        {useCase.title}
      </h3>
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isActive ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p className="text-sm pad:text-base text-white/90 leading-relaxed">
            {useCase.description}
          </p>
        </div>
      </div>
    </button>
  );
};

const UseCasesSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const activeUseCase = useCases[activeIndex];

  const handleTabClick = (index: number) => {
    setActiveIndex(index);
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const handlePlayClick = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleVideoClick = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const handleVideoEnded = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
    setIsPlaying(false);
  };

  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Real-World Use Cases of AI Avatars
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          FalcoCut AI Avatar Generator is built for diverse industries and creators. From
          e-commerce brands driving sales to educators creating interactive lessons, our
          avatars bring ideas to life in ways that feel authentic and engaging. Explore
          how businesses, teachers, and creators use avatars to scale content, connect
          globally, and save time.
        </p>

        {/* Content */}
        <div className="flex flex-col pad:flex-row gap-6 pad:gap-8 sm:gap-12 items-stretch">
          {/* Left - Video */}
          <div className="w-full pad:w-1/2 flex items-center justify-center">
            <div className="relative w-full rounded-2xl overflow-hidden bg-black">
              <video
                ref={videoRef}
                key={activeUseCase.videoUrl}
                src={activeUseCase.videoUrl}
                poster={activeUseCase.posterUrl}
                className="w-full h-auto cursor-pointer"
                onEnded={handleVideoEnded}
                onClick={handleVideoClick}
                playsInline
              />
              {/* Play Button Overlay */}
              {!isPlaying && (
                <button
                  type="button"
                  onClick={handlePlayClick}
                  className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors cursor-pointer"
                  aria-label="Play video"
                >
                  <PlayButton />
                </button>
              )}
            </div>
          </div>

          {/* Right - Tabs */}
          <div className="w-full pad:w-1/2 flex flex-col gap-3 pad:gap-4">
            {useCases.map((useCase, index) => (
              <UseCaseTab
                key={useCase.id}
                useCase={useCase}
                isActive={index === activeIndex}
                onClick={() => handleTabClick(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
